import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../../utils/i18n';
import {DroppableTypes} from '../../../constants/droppable';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import Button from '@adsk/bim360-matrix-react-button';
import InlineEditTextField from '@adsk/bim360-matrix-react-inline-edit-text-field';
import {ISSUE_ATTRIBUTE_MAX_LIST_ITEM_ITEMS_LENGTH} from '../../../constants/issue-attributes';
import IssueAttributesAdminDraggableListItem from './IssueAttributesAdminDraggableListItem';
import {isIssueAttributeListItemValid} from '../../../utils/issueAttributes-helpers';

class IssueAttributesAdminDraggableList extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      typing: false,
      options: props.options
    };

    this.onDragEnd = this.onDragEnd.bind(this);
    this.renderDraggableItem = this.renderDraggableItem.bind(this);
    this.onAnswerEditEnd = this.onAnswerEditEnd.bind(this);
    this.onAnswerChange = this.onAnswerChange.bind(this);
    this.onAnswerRemove = this.onAnswerRemove.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.options !== nextProps.options) {
      this.setState({options: nextProps.options});
    }
  }

  hasReachedOptionsLimit() {
    return this.state.options.length >= ISSUE_ATTRIBUTE_MAX_LIST_ITEM_ITEMS_LENGTH;
  }

  onDragEnd(result) {
    if (!result.destination) {
      return;
    }

    switch (result.type) {
      case DroppableTypes.ISSUE_ATTRIBUTE_LIST_ITEM: {
        if (result.source.droppableId !== result.destination.droppableId) {
          return;
        }
        const itemSourceIndex = result.source.index;
        const itemDestinationIndex = result.destination.index;
        if (itemSourceIndex !== itemDestinationIndex) {
          this.switchListItem(itemSourceIndex, itemDestinationIndex);
        }
        break;
      }

      default:
        break;
    }
  }

  updateAnswers() {
    this.props.onChange(this.state.options);
  }

  switchListItem(fromIndex, toIndex) {
    const newOptions = [...this.state.options];
    const [removed] = newOptions.splice(fromIndex, 1);
    newOptions.splice(toIndex, 0, removed);
    this.setState({options: newOptions}, () => {
      this.updateAnswers();
    });
  }

  addAnswer() {
    this.setState({options: [...this.state.options, {value: '', isNew: true, valid: false}]}, () => {
      this.updateAnswers();
    });
  }

  onAnswerRemove(i) {
    this.props.onAnswerRemove(i);
  }

  onAnswerEditEnd(i, value, isCancel, initialValue, inlineEditFinisher, isValid) {
    const valid = isValid && value.length > 0;
    // Add new item if current is valid and finisher key is enter
    const addNewItem =
      !this.hasReachedOptionsLimit() && inlineEditFinisher === InlineEditTextField.InlineEditFinishers.ENTER && valid;
    const newOptions = [...this.state.options];
    // Remove answer if initialValue was empty & current value is empty
    if (i > 0 && !value && value === initialValue) {
      newOptions.splice(i, 1);
    } else {
      newOptions.splice(i, 1, {...newOptions[i], value, valid, isNew: false});
    }
    this.setState({options: newOptions, typing: false}, () => {
      if (addNewItem && !this.addAnswerClicked) {
        this.addAnswer();
      } else {
        this.updateAnswers();
      }
    });
  }

  onAnswerChange(i, answer, value) {
    if (answer.value !== value) {
      const newOptions = [...this.state.options];
      newOptions.splice(i, 1, {
        ...newOptions[i],
        value,
        isNew: false,
        valid: this.isOptionValid(this.state.options, value, i)
      });
      this.setState({options: newOptions, typing: value.length > 0});
    }
  }

  isOptionValid(options, value, index) {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    const values = newOptions.map(option => option.value.trim().toLowerCase());
    return isIssueAttributeListItemValid(value, values);
  }

  renderDraggableItem(value, i) {
    const {container} = this.props;
    const {options} = this.state;
    const isDragDisabled = !options || options.length === 1;

    return (
      <Draggable key={`${value}-${i}`} draggableId={`${value}-${i}`} index={i} isDragDisabled={isDragDisabled}>
        {(provided, snapshot) => (
          <IssueAttributesAdminDraggableListItem
            provided={provided}
            container={container}
            snapshot={snapshot}
            isDragDisabled={isDragDisabled}
            answer={value}
            index={i}
            options={this.state.options}
            onAnswerEditEnd={this.onAnswerEditEnd}
            onAnswerRemove={this.onAnswerRemove}
            onAnswerChange={this.onAnswerChange}
          />
        )}
      </Draggable>
    );
  }

  renderAddAnswer() {
    const isDisabled = this.state.options.some(option => !option.valid);
    return (
      <div className="IssueAttributesAdminDraggableList__add-answer">
        {this.hasReachedOptionsLimit() ? (
          <div
            className="IssueAttributesAdminDraggableList__instructions"
            data-testid="IssueAttributesAdminDraggableList__options-limit-reached"
          >
            {i18n.t('issueAttributesAdmin.modal.optionsLimitReached')}
          </div>
        ) : (
          <Button
            styleType={Button.StyleType.LINK}
            className="IssueAttributesAdminDraggableList__add-answer-button"
            disabled={isDisabled}
            data-testid="IssueAttributesAdminDraggableList__add-answer-button"
            onMouseDown={() => {
              // Prevents creation of new answers on click outside, caused by drag & drop
              this.addAnswerClicked = true;
              setTimeout(() => {
                this.addAnswer();
                this.addAnswerClicked = false;
              });
            }}
          >
            {i18n.t('issueAttributesAdmin.modal.addAnswer')}
          </Button>
        )}
        <div
          className="IssueAttributesAdminDraggableList__instructions"
          data-testid="IssueAttributesAdminDraggableList__press-enter-to-create"
        >
          {this.state.typing ? i18n.t('issueAttributesAdmin.modal.instructions') : ''}
        </div>
      </div>
    );
  }

  render() {
    return (
      <DragDropContext onDragEnd={this.onDragEnd}>
        <Droppable
          droppableId={`${DroppableTypes.ISSUE_ATTRIBUTE_LIST_ITEM}-1`}
          type={DroppableTypes.ISSUE_ATTRIBUTE_LIST_ITEM}
        >
          {provided => (
            <div
              className="IssueAttributesAdminDraggableList"
              data-testid="IssueAttributesAdminDraggableList"
              ref={provided.innerRef}
            >
              <div>
                {this.state.options && this.state.options.map(this.renderDraggableItem)}
                {this.renderAddAnswer()}
              </div>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  }
}

IssueAttributesAdminDraggableList.displayName = 'IssueAttributesAdminDraggableList';

IssueAttributesAdminDraggableList.propTypes = {
  container: PropTypes.object.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  onAnswerRemove: PropTypes.func.isRequired
};

export default IssueAttributesAdminDraggableList;
